import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerProfessionalCertificateCareerOutcomesModuleRenderer';
import FAQsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/faqs/FAQsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerSpecializationProductOverviewModuleRenderer';
import SyllabusModuleRenderer from 'bundles/unified-description-page-common/module-renderers/syllabus/SpecializationSyllabusModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/ConsumerUpsellModuleRenderer';
import type { ConsumerSpecializationMarketingPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
  UpsellCardNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';

type Props = {
  pageData: ConsumerSpecializationMarketingPageFieldsFragment;
  pageConfig?: PageConfig | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                removeLinksAboveFold: true,
                showHeroLanguageInfo: false,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ContentCount },
                    { name: KeyInformationItemNames.Reviews },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.Schedule },
                    { name: KeyInformationItemNames.Stackability },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCertificate,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        return <CareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return (
          <SyllabusModuleRenderer
            pageData={pageData}
            config={{ disableCta: true, disableAceCta: true }}
            key={moduleConfig.name}
          />
        );
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{
              upsellCards: [
                UpsellCardNames.CourseraPlusUpsellCard,
                UpsellCardNames.DegreeUpsellCard,
                UpsellCardNames.EnterpriseUpsellCard,
              ],
            }}
          />
        );
      case ModuleNames.Faqs:
        return <FAQsModuleRenderer pageData={pageData} config={{ disableCta: true }} key={moduleConfig.name} />;
      case ModuleNames.PageBanner:
        return <PageBannerModuleRenderer key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
